import React, { useEffect, useState } from "react";
import useAdminDataStore, { selectCurrentGroupData } from "../../../../../store/adminData/adminData";
import { Spin } from "antd";

// import SmokeImage from "../../../../../images/promote/Smoke-Effect.png";
import SmokeImage from "./upscaled-3.jpg";
import BallImage from "../../../../../images/promote/Ball-Chant.svg";
import ChantLogo from "../../../../../images/promote/Chant-Logo.png";
import PhoneSkeletonFlat from "../../../../../images/promote/iPhone-Flat.svg";
import ChantBrandmark from "../../../../../images/promote/Chant-Brandmark.png";
import {
  rgbaToHex,
  darkenRGBColor,
  rgbStringToObject,
  brightenRGBColor,
  isLightColor,
  isDarkColor,
  lightenRGBColor,
} from "../../../../../utils/colors";

const CANVAS_CONFIG = {
  WIDTH: 400,
  HEIGHT: 500,
  DISPLAY_WIDTH: 400,
  DISPLAY_HEIGHT: 500,
  SCALE_FACTOR: 3,
  GRADIENT_HEIGHT: 700,
  BOTTOM_PANEL_HEIGHT: 22,
};

const FONT_SIZES = {
  GROUP_NAME: 20,
  PROUDLY: 50,
  POWERED_BY: 50,
  CHANT_WEBSITE: 15,
};

const PHONE_CONFIG = {
  WIDTH: 250,
  HEIGHT: 400,
  POSITION_X: 215,
  POSITION_Y_OFFSET: 40,
  UI_POSITION_OFFSET: 10,
  UI_DIMENSION_OFFSET: 15,
  SCALE: 0.3,
  BALL_SCALE: 0.5,
  LOGO_CIRCLE_RADIUS: 90,
  LOGO_SCALE_FACTOR: 0.6,
};

const FONTS = {
  OSWALD: "Oswald",
  OPEN_SANS: "Open Sans",
};

// Font loading utility
const loadFonts = async () => {
  try {
    const fonts = [
      {
        family: FONTS.OSWALD,
        url: "https://fonts.gstatic.com/s/oswald/v49/TK3_WkUHHAIjg75cFRf3bXL8LICs1_FvsUZiYA.ttf",
      },
      {
        family: FONTS.OPEN_SANS,
        url: "https://fonts.gstatic.com/s/opensans/v34/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsjZ0B4gaVI.woff2",
      },
    ];

    const fontPromises = fonts.map(async ({ family, url }) => {
      const font = new FontFace(family, `url(${url})`);
      const loadedFont = await font.load();
      document.fonts.add(loadedFont);
      return loadedFont;
    });

    await Promise.all(fontPromises);
    return true;
  } catch (error) {
    console.error("Error loading fonts:", error);
    return false;
  }
};

// Canvas drawing utilities - Pure functions
const fillBackgroundGradient = (ctx, primaryColor) => {
  const { r, g, b } = primaryColor;
  const gradient = ctx.createLinearGradient(
    0,
    0,
    0,
    CANVAS_CONFIG.GRADIENT_HEIGHT,
  );
  gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 1)`);
  gradient.addColorStop(0.66, "#1a1a1a");
  ctx.fillStyle = gradient;
  ctx.fillRect(
    0,
    0,
    CANVAS_CONFIG.GRADIENT_HEIGHT,
    CANVAS_CONFIG.GRADIENT_HEIGHT,
  );
};

const drawSmokeEffect = (ctx, smokeImage, primaryColor) => {
  let mainColor = `rgb(${primaryColor.r}, ${primaryColor.g}, ${primaryColor.b})`;

  if (isDarkColor(primaryColor, 0.3)) {
    mainColor = lightenRGBColor(primaryColor, 0.3);
  }

  if (isDarkColor(primaryColor, 0.02)) {
    mainColor = lightenRGBColor(primaryColor, 0.5);
  }

  if (isLightColor(primaryColor, 0.3)) {
    mainColor = darkenRGBColor(primaryColor, 0.05);
  }

  // Redraw background with opacity for smoke
  const gradient = ctx.createLinearGradient(
    0,
    0,
    0,
    CANVAS_CONFIG.GRADIENT_HEIGHT,
  );
  gradient.addColorStop(0, mainColor);
  gradient.addColorStop(0.66, `rgba(26, 26, 26, 0.9)`);
  ctx.fillStyle = gradient;
  ctx.fillRect(
    0,
    0,
    CANVAS_CONFIG.GRADIENT_HEIGHT,
    CANVAS_CONFIG.GRADIENT_HEIGHT,
  );

  // Add Smoke Image
  ctx.globalCompositeOperation = "multiply";
  ctx.fillStyle = gradient;
  ctx.fillRect(-300, -250, 1200, 1200);
  ctx.globalCompositeOperation = "overlay";

  // Scale and center the smoke image
  const scale = 0.5;
  const drawW = smokeImage.naturalWidth * scale;
  const drawH = smokeImage.naturalHeight * scale;
  const x = (CANVAS_CONFIG.WIDTH - drawW) / 2 + 70;
  const y = (CANVAS_CONFIG.HEIGHT - drawH) / 2 + 70;
  ctx.drawImage(smokeImage, x, y, drawW, drawH);

  // Reset composite operation
  ctx.globalCompositeOperation = "source-over";
};

const drawBallBackgroundCircle = (ctx, primaryColor) => {
  const { r, g, b } = primaryColor;
  const lighterColor = rgbStringToObject(lightenRGBColor(primaryColor, 0.1));

  ctx.beginPath();
  ctx.arc(200, 500, 200, Math.PI, 2 * Math.PI);

  const radialGradient = ctx.createRadialGradient(200, 400, 0, 200, 400, 400);
  radialGradient.addColorStop(
    0,
    `rgba(${lighterColor.r}, ${lighterColor.g}, ${lighterColor.b}, 0.7)`,
  );
  radialGradient.addColorStop(
    0.6,
    `rgba(${lighterColor.r}, ${lighterColor.g}, ${lighterColor.b}, 0.18)`,
  );
  radialGradient.addColorStop(0.85, `rgba(${r}, ${g}, ${b}, 0.01)`);
  radialGradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);

  ctx.fillStyle = radialGradient;
  ctx.fill();
};

const drawBottomPanel = (ctx, secondaryColor) => {
  const { r, g, b } = secondaryColor;
  const isLight = isLightColor(secondaryColor);

  ctx.beginPath();
  ctx.rect(
    0,
    CANVAS_CONFIG.HEIGHT - CANVAS_CONFIG.BOTTOM_PANEL_HEIGHT,
    CANVAS_CONFIG.WIDTH,
    CANVAS_CONFIG.BOTTOM_PANEL_HEIGHT,
  );

  ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
  ctx.fill();

  const baselineY =
    CANVAS_CONFIG.HEIGHT - CANVAS_CONFIG.BOTTOM_PANEL_HEIGHT / 2 + 1;
  const elements = ["www.chant.fan", "FUEL THE HEART OF FOOTBALL"];

  // Calculate total width of one complete set
  ctx.textAlign = "left";
  ctx.font = "10px Oswald";
  ctx.letterSpacing = "0.08em";
  ctx.textBaseline = "middle";
  ctx.fillStyle = isLight ? "#000" : "#fff";

  const elementWidths = elements.map((el) => ctx.measureText(el).width);

  // Calculate dot width with larger font
  ctx.font = "24px Oswald";
  const dotWidth = ctx.measureText("●").width;
  const spacing = 4;

  // Calculate width of one complete set
  const setWidth =
    elementWidths[0] + spacing + dotWidth + spacing + elementWidths[1];
  const totalWidth = setWidth * 3 + spacing * 4;
  let currentX = CANVAS_CONFIG.WIDTH - totalWidth + 18;

  // Draw three sets of elements
  for (let set = 0; set < 3; set++) {
    elements.forEach((element, index) => {
      ctx.font = "10px Oswald";
      ctx.fillText(element, currentX, baselineY);
      currentX += elementWidths[index] + spacing;

      if (!(set === 2 && index === elements.length - 1)) {
        ctx.textBaseline = "middle";
        ctx.font = "14px Oswald";
        const dotY = baselineY - 1;
        ctx.fillText("●", currentX, dotY);
        currentX += dotWidth - 2;
      }
    });
  }
};

// Text utilities
const drawWrappedText = (ctx, text, x, y, maxWidth, lineHeight) => {
  const words = text.split(" ");
  let line = "";
  let testLine = "";
  let lineY = y;
  let lineCount = 0;

  for (let n = 0; n < words.length; n++) {
    testLine = line + words[n] + " ";
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && n > 0) {
      ctx.fillText(line, x, lineY);
      line = words[n] + " ";
      lineY += lineHeight;
      lineCount++;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line, x, lineY);
  lineCount++;
  return lineCount;
};

const countWrappedLines = (ctx, text, maxWidth) => {
  const words = text.split(" ");
  let line = "";
  let wraps = 1;

  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + " ";
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && n > 0) {
      line = words[n] + " ";
      wraps++;
    } else {
      line = testLine;
    }
  }
  return wraps;
};

const generateTextBlock = (
  ctx,
  logoImg,
  chantLogoImg,
  groupName,
  isLogoTransparent,
) => {
  const text = groupName;
  ctx.font = `400 ${FONT_SIZES.GROUP_NAME}px Oswald`;
  ctx.textBaseline = "middle";
  ctx.letterSpacing = "0.04em";

  const startX = 20;
  const baseRow1Y = 65;
  const logoCircleRadius = 20;
  const maxTextWidth = 150;
  const lineHeight = 25;

  const lines = countWrappedLines(ctx, text, maxTextWidth);
  const offset = (lines - 1) * lineHeight;
  const row1Y = baseRow1Y;

  // Draw the logo
  const logoScaleX = (logoCircleRadius * 2) / logoImg.naturalWidth;
  const logoScaleY = (logoCircleRadius * 2) / logoImg.naturalHeight;
  const finalLogoScale = Math.min(logoScaleX, logoScaleY);
  const finalLogoWidth = logoImg.naturalWidth * finalLogoScale;
  const finalLogoHeight = logoImg.naturalHeight * finalLogoScale;

  ctx.save();
  ctx.beginPath();
  ctx.arc(
    startX + logoCircleRadius,
    row1Y + offset / 2,
    logoCircleRadius,
    0,
    2 * Math.PI,
  );
  if (!isLogoTransparent) {
    ctx.clip();
  }
  ctx.drawImage(
    logoImg,
    startX + logoCircleRadius - finalLogoWidth / 2,
    row1Y - finalLogoHeight / 2 + offset / 2,
    finalLogoWidth,
    finalLogoHeight,
  );
  ctx.restore();

  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "#fff";

  drawWrappedText(
    ctx,
    text,
    startX + logoCircleRadius * 2 + 10,
    row1Y + FONT_SIZES.GROUP_NAME / 10,
    maxTextWidth,
    lineHeight,
  );

  const row2Y = row1Y + 90 + offset;
  ctx.font = `400 ${FONT_SIZES.PROUDLY}px Oswald`;
  ctx.textAlign = "left";
  ctx.letterSpacing = "0.01em";
  ctx.fillText("Proudly", startX, row2Y);

  const row3Y = row2Y + 50;
  ctx.font = `400 ${FONT_SIZES.POWERED_BY}px Oswald`;
  ctx.textAlign = "left";
  ctx.letterSpacing = "0.01em";
  ctx.fillText("powered", startX, row3Y);

  const row4Y = row3Y + 60;
  ctx.font = `400 ${FONT_SIZES.POWERED_BY}px Oswald`;
  ctx.textAlign = "left";
  ctx.letterSpacing = "0.01em";
  ctx.fillText("by", startX, row4Y);

  const row5Y = row4Y + 80;
  ctx.drawImage(
    chantLogoImg,
    startX,
    row5Y,
    chantLogoImg.naturalWidth * (120 / chantLogoImg.naturalWidth),
    chantLogoImg.naturalHeight * (120 / chantLogoImg.naturalWidth),
  );

  const row6Y = row5Y + 55;
  ctx.font = `${FONT_SIZES.CHANT_WEBSITE}px Open Sans`;
  ctx.save();
  ctx.globalAlpha = 0.8;
  ctx.fillStyle = "#fff";
  ctx.textAlign = "left";
  ctx.fillText("www.chant.fan", startX + 5, row6Y);
  ctx.restore();
};

// Phone drawing
const drawFlatPhone = (
  ctx,
  phoneSkeletonFlat,
  ballImage,
  groupLogo,
  brandmarkImage,
  groupName,
  color,
  isLogoTransparent,
) => {
  const { r, g, b } = color;
  const primaryColor = `rgb(${r}, ${g}, ${b})`;
  const lightColor = lightenRGBColor(color, 0.3);
  const darkerColor = darkenRGBColor(color, 0.3);
  const text = groupName;

  const phonePositionX = PHONE_CONFIG.POSITION_X;
  const phonePositionY =
    CANVAS_CONFIG.HEIGHT - PHONE_CONFIG.HEIGHT + PHONE_CONFIG.POSITION_Y_OFFSET;

  // Draw UI background
  const scale = PHONE_CONFIG.WIDTH / phoneSkeletonFlat.naturalWidth;
  const borderRadius = 40 * scale;

  ctx.save();
  ctx.beginPath();
  ctx?.roundRect(
    phonePositionX + PHONE_CONFIG.UI_POSITION_OFFSET,
    phonePositionY - 90,
    PHONE_CONFIG.WIDTH - PHONE_CONFIG.UI_DIMENSION_OFFSET,
    PHONE_CONFIG.HEIGHT + 100,
    borderRadius,
  );
  ctx.closePath();

  const gradient = ctx.createLinearGradient(0, 0, 0, PHONE_CONFIG.HEIGHT);
  const firstColorStop = isDarkColor(color) ? lightColor : primaryColor;
  const secondColorStop = isDarkColor(color) ? primaryColor : darkerColor;

  gradient.addColorStop(0, firstColorStop);
  gradient.addColorStop(0.7, secondColorStop);

  ctx.fillStyle = gradient;
  ctx.fill();
  ctx.restore();

  // Draw group logo
  const scaleX = (PHONE_CONFIG.LOGO_CIRCLE_RADIUS * 2) / groupLogo.naturalWidth;
  const scaleY =
    (PHONE_CONFIG.LOGO_CIRCLE_RADIUS * 2) / groupLogo.naturalHeight;

  // For transparent logos, use Math.min to ensure entire logo fits within circle bounds
  // For regular logos, use Math.max to fill the circle (will be clipped anyway)
  const logoScale =
    (isLogoTransparent ? Math.min(scaleX, scaleY) : Math.max(scaleX, scaleY)) *
    PHONE_CONFIG.LOGO_SCALE_FACTOR;

  const logoDrawWidth = groupLogo.naturalWidth * logoScale;
  const logoDrawHeight = groupLogo.naturalHeight * logoScale;

  const lines = countWrappedLines(ctx, text, 140);
  const offset = lines > 1 ? lines * 30 : 0;

  // Get phone dimensions for proper centering
  const scaledPhoneWidth = phoneSkeletonFlat.naturalWidth * PHONE_CONFIG.SCALE;
  const circleRadius = PHONE_CONFIG.LOGO_CIRCLE_RADIUS;

  // Center the logo within the phone area
  const circleCenterX = phonePositionX + scaledPhoneWidth / 2;
  const circleCenterY = CANVAS_CONFIG.HEIGHT / 3 + 70 - 20 - offset;

  ctx.save();
  ctx.beginPath();
  ctx.arc(circleCenterX, circleCenterY, circleRadius, 0, 2 * Math.PI);
  ctx.closePath();
  if (!isLogoTransparent) {
    ctx.beginPath();
    ctx.arc(circleCenterX, circleCenterY, logoDrawWidth / 2, 0, 2 * Math.PI);
    ctx.clip();
  }

  // Center the logo within the circular area
  ctx.drawImage(
    groupLogo,
    circleCenterX - logoDrawWidth / 2,
    circleCenterY - logoDrawHeight / 2,
    logoDrawWidth,
    logoDrawHeight,
  );
  ctx.restore();

  // Draw group name
  ctx.font = "500 20px Oswald";
  ctx.letterSpacing = "0.02rem";
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";

  const scaledPhoneHeight =
    phoneSkeletonFlat.naturalHeight * PHONE_CONFIG.SCALE;

  drawWrappedText(
    ctx,
    text,
    phonePositionX + scaledPhoneWidth / 2,
    CANVAS_CONFIG.HEIGHT - 200 - offset,
    scaledPhoneWidth - 75,
    30,
  );

  // Draw ball image
  ctx.drawImage(
    ballImage,
    phonePositionX - 60,
    CANVAS_CONFIG.HEIGHT - 150,
    (ballImage.naturalWidth * PHONE_CONFIG.WIDTH * PHONE_CONFIG.BALL_SCALE) /
      CANVAS_CONFIG.WIDTH,
    (ballImage.naturalHeight * (PHONE_CONFIG.WIDTH * PHONE_CONFIG.BALL_SCALE)) /
      CANVAS_CONFIG.WIDTH,
  );

  // Draw brandmark
  ctx.drawImage(
    brandmarkImage,
    phonePositionX + scaledPhoneWidth / 2 - 30,
    CANVAS_CONFIG.HEIGHT - 90,
    50,
    brandmarkImage.naturalHeight * (50 / brandmarkImage.naturalWidth),
  );

  // Draw phone skeleton
  ctx.drawImage(
    phoneSkeletonFlat,
    phonePositionX,
    phonePositionY - 100,
    scaledPhoneWidth,
    scaledPhoneHeight,
  );

  ctx.beginPath();
  ctx.rect(
    phonePositionX + 17,
    phonePositionY,
    PHONE_CONFIG.WIDTH + 20,
    PHONE_CONFIG.HEIGHT,
  );
  ctx.clip();
};

// Image loading utilities - FIXED VERSION
const loadColoredBallImage = async (
  ballImage,
  primaryColor,
  brightness = 1,
) => {
  try {
    const adjustedColor =
      brightness > 1
        ? brightenRGBColor(primaryColor, brightness - 1)
        : darkenRGBColor(primaryColor, 1 - brightness);

    const rgbObject = rgbStringToObject(adjustedColor);
    const hexColor = rgbaToHex(rgbObject.r, rgbObject.g, rgbObject.b);

    const response = await fetch(BallImage);
    const svgXml = await response.text();
    const coloredSvgXml = svgXml
      .replace(/stroke="[^"]*"/g, `stroke="${hexColor}"`)
      .replace(/fill="[^"]*"/g, `fill="${hexColor}"`);

    // ✅ FIX: Return a promise that resolves when image is actually loaded
    return new Promise((resolve, reject) => {
      let resolved = false;

      const handleLoad = () => {
        if (!resolved) {
          resolved = true;
          resolve(ballImage);
        }
      };

      const handleError = (error) => {
        if (!resolved) {
          resolved = true;
          console.error("Error loading ball image:", error);
          reject(error);
        }
      };

      ballImage.onload = handleLoad;
      ballImage.onerror = handleError;

      // Set the source
      ballImage.src =
        "data:image/svg+xml;charset=utf-8," + encodeURIComponent(coloredSvgXml);

      // ✅ Safari-specific fix: Sometimes image loads synchronously or onload doesn't fire
      // Check after a short delay if image is already complete
      setTimeout(() => {
        if (ballImage.complete && ballImage.naturalWidth > 0 && !resolved) {
          resolved = true;
          resolve(ballImage);
        }
      }, 100);

      // ✅ Timeout fallback: If image doesn't load within 5 seconds, reject
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          reject(new Error("Ball image load timeout"));
        }
      }, 5000);
    });
  } catch (error) {
    console.error("Error in loadColoredBallImage:", error);
    throw error;
  }
};

const createImageLoader = (imageSources) => {
  const images = {};

  const loadImage = (key, src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        images[key] = img;
        resolve(img);
      };

      img.onerror = (error) => {
        console.error(`Failed to load image: ${key}`, error);
        reject(error);
      };

      img.src = src;

      // ✅ Timeout fallback
      setTimeout(() => {
        if (img.complete && img.naturalWidth > 0) {
          if (!images[key]) {
            images[key] = img;
            resolve(img);
          }
        }
      }, 100);
    });
  };

  const promises = Object.entries(imageSources).map(([key, src]) =>
    loadImage(key, src),
  );

  return Promise.all(promises)
    .then(() => images)
    .catch((error) => {
      console.error("Error loading some images:", error);
      return images; // Return partial results
    });
};
// ✅ NEW: Helper function to verify image is truly loaded
const verifyImageLoaded = (image, timeoutMs = 500) => {
  return new Promise((resolve) => {
    if (image.complete && image.naturalWidth > 0 && image.naturalHeight > 0) {
      resolve(true);
      return;
    }

    let timeoutId;
    const checkInterval = setInterval(() => {
      if (image.complete && image.naturalWidth > 0 && image.naturalHeight > 0) {
        clearInterval(checkInterval);
        clearTimeout(timeoutId);
        resolve(true);
      }
    }, 50);

    timeoutId = setTimeout(() => {
      clearInterval(checkInterval);
      console.warn("Image verification timeout:", image.src?.substring(0, 100));
      resolve(false);
    }, timeoutMs);
  });
};

// Main component
const SingularPromoteTemplate = ({
  posterType,
  canvasRef,
  hasSingularLoaded,
  setHasSingularLoaded,
}) => {
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // Font loading effect
  useEffect(() => {
    const initializeFonts = async () => {
      const success = await loadFonts();
      setFontsLoaded(success);
    };
    initializeFonts();
  }, []);

  // Main rendering effect
  useEffect(() => {
    if (!fontsLoaded || !groupData) return;

    const renderCanvas = async () => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Verify fonts are loaded
      ctx.font = "500 32px Oswald";
      const metrics = ctx.measureText("Test");
      if (metrics.width === 0) {
        setTimeout(() => setFontsLoaded(false), 100);
        return;
      }

      // Setup canvas
      canvas.width = CANVAS_CONFIG.WIDTH * CANVAS_CONFIG.SCALE_FACTOR;
      canvas.height = CANVAS_CONFIG.HEIGHT * CANVAS_CONFIG.SCALE_FACTOR;
      canvas.style.width = `${CANVAS_CONFIG.DISPLAY_WIDTH}px`;
      canvas.style.height = `${CANVAS_CONFIG.DISPLAY_HEIGHT}px`;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(CANVAS_CONFIG.SCALE_FACTOR, CANVAS_CONFIG.SCALE_FACTOR);

      setHasSingularLoaded(false);

      const { primaryColor, secondaryColor } = groupData.configuration;
      const optionBasedColor =
        posterType === "option1" ? primaryColor : secondaryColor;

      // Load images
      const imageSources = {
        smoke: SmokeImage,
        logo: groupData.configuration.logo,
        chantLogo: ChantLogo,
        phoneSkeleton: PhoneSkeletonFlat,
        brandmark: ChantBrandmark,
      };

      try {
        const images = await createImageLoader(imageSources);

        // ✅ Verify all base images are loaded
        const baseImagesLoaded = await Promise.all([
          verifyImageLoaded(images.smoke),
          verifyImageLoaded(images.logo),
          verifyImageLoaded(images.chantLogo),
          verifyImageLoaded(images.phoneSkeleton),
          verifyImageLoaded(images.brandmark),
        ]);

        if (!baseImagesLoaded.every(Boolean)) {
          console.warn("Some base images failed to load completely");
        }

        // Create ball images
        const phoneBallImage = new Image();
        const posterBallImage = new Image();
        phoneBallImage.crossOrigin = "anonymous";
        posterBallImage.crossOrigin = "anonymous";

        // ✅ Wait for ball images to ACTUALLY load (not just fetch)
        await Promise.all([
          loadColoredBallImage(
            phoneBallImage,
            isDarkColor(primaryColor)
              ? rgbStringToObject(lightenRGBColor(primaryColor, 0.3))
              : primaryColor,
            1.4,
          ),
          loadColoredBallImage(
            posterBallImage,
            isDarkColor(optionBasedColor)
              ? rgbStringToObject(lightenRGBColor(optionBasedColor, 0.3))
              : optionBasedColor,
            1.2,
          ),
        ]).catch((error) => {
          console.error("Failed to load ball images:", error);
        });

        // ✅ Additional verification for ball images (especially for Safari)
        const ballImagesReady = await Promise.all([
          verifyImageLoaded(phoneBallImage, 500),
          verifyImageLoaded(posterBallImage, 500),
        ]);

        if (!ballImagesReady.every(Boolean)) {
          console.warn("Ball images verification failed, waiting longer...");
          await new Promise((resolve) => setTimeout(resolve, 300));
        }

        // ✅ Final check before rendering
        const allCriticalImagesReady =
          phoneBallImage.complete &&
          phoneBallImage.naturalWidth > 0 &&
          posterBallImage.complete &&
          posterBallImage.naturalWidth > 0 &&
          images.smoke.complete &&
          images.smoke.naturalWidth > 0 &&
          images.logo.complete &&
          images.logo.naturalWidth > 0;

        if (!allCriticalImagesReady) {
          console.warn(
            "Critical images not ready, attempting render anyway...",
            {
              phoneBall: {
                complete: phoneBallImage.complete,
                width: phoneBallImage.naturalWidth,
              },
              posterBall: {
                complete: posterBallImage.complete,
                width: posterBallImage.naturalWidth,
              },
            },
          );
        }

        // Render everything using pure functions
        fillBackgroundGradient(ctx, primaryColor);
        drawSmokeEffect(ctx, images.smoke, optionBasedColor);
        drawBallBackgroundCircle(ctx, optionBasedColor);

        // Draw poster ball
        const ballAspectRatio =
          posterBallImage.naturalWidth / posterBallImage.naturalHeight;
        const ballWidth = CANVAS_CONFIG.WIDTH;
        const ballHeight = ballWidth / ballAspectRatio;
        const ballY = 300;
        ctx.globalCompositeOperation = "source-over";
        ctx.drawImage(posterBallImage, 0, ballY, ballWidth, ballHeight);

        generateTextBlock(
          ctx,
          images.logo,
          images.chantLogo,
          groupData.groupName,
          groupData?.configuration?.isLogoTransparent,
        );
        drawBottomPanel(ctx, secondaryColor);

        // Draw phone
        ctx.beginPath();
        const x = 218;
        const y = 0;
        const width = images.phoneSkeleton.naturalWidth;
        const height = CANVAS_CONFIG.HEIGHT - 22;
        const radius = 15;

        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width, y);
        ctx.lineTo(x + width, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y);
        ctx.closePath();
        ctx.clip();

        drawFlatPhone(
          ctx,
          images.phoneSkeleton,
          phoneBallImage,
          images.logo,
          images.brandmark,
          groupData.groupName,
          primaryColor,
          groupData?.configuration?.isLogoTransparent,
        );

        // ✅ Small delay to ensure rendering completes before hiding loader
        await new Promise((resolve) => setTimeout(resolve, 100));

        setHasSingularLoaded(true);
      } catch (error) {
        console.error("Error rendering canvas:", error);
        setHasSingularLoaded(true); // Still set to true to avoid infinite loading
      }
    };

    renderCanvas();
  }, [groupData, fontsLoaded, posterType, canvasRef, setHasSingularLoaded]);

  return (
    <div
      style={{
        width: CANVAS_CONFIG.WIDTH,
        height: CANVAS_CONFIG.HEIGHT,
        position: "relative",
      }}
    >
      <canvas
        ref={canvasRef}
        width={CANVAS_CONFIG.WIDTH * CANVAS_CONFIG.SCALE_FACTOR}
        height={CANVAS_CONFIG.HEIGHT * CANVAS_CONFIG.SCALE_FACTOR}
        style={{
          width: `${CANVAS_CONFIG.DISPLAY_WIDTH}px`,
          height: `${CANVAS_CONFIG.DISPLAY_HEIGHT}px`,
          display: hasSingularLoaded ? "block" : "none",
        }}
      />
      {(!hasSingularLoaded || !fontsLoaded) && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: `${CANVAS_CONFIG.DISPLAY_WIDTH}px`,
            height: `${CANVAS_CONFIG.DISPLAY_HEIGHT}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(163, 163, 163, 0.7)",
            color: "#fff",
            zIndex: 2000,
            fontSize: 24,
            fontWeight: "bold",
            opacity: 1,
            transition: "opacity 0.5s ease-out",
            pointerEvents: "none",
          }}
        >
          <Spin size="large" color="#ffffff" />
        </div>
      )}
    </div>
  );
};

export default SingularPromoteTemplate;
